/**
 * PHI Sanitization Utility
 *
 * This utility provides functions to sanitize Protected Health Information (PHI)
 * before sending to analytics/tracking systems to ensure HIPAA compliance.
 *
 * @reasoning
 * - Healthcare data requires strict PHI handling
 * - All personally identifiable information must be hashed or removed
 * - Tracking IDs and non-PHI data can remain
 * - One-way hashing prevents data recovery while maintaining uniqueness
 * - Uses Web Crypto API for browser compatibility
 */

/**
 * Hash a string value using SHA-256 (Web Crypto API)
 * One-way hashing ensures data cannot be recovered
 * Browser-compatible implementation using crypto.subtle
 */
async function hashValue(value: string): Promise<string> {
  if (!value) return '';

  // Convert string to ArrayBuffer
  const encoder = new TextEncoder();
  const data = encoder.encode(value);

  // Hash using Web Crypto API
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Convert ArrayBuffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return hashHex.substring(0, 16);
}

/**
 * Sanitize PHI fields from tracking data
 * Removes or hashes all personally identifiable information
 *
 * @param data - Raw tracking data that may contain PHI
 * @returns Sanitized data safe for analytics
 */
export async function sanitizeTrackingData<T extends Record<string, any>>(
  data: T,
): Promise<Record<string, any>> {
  const sanitized: Record<string, any> = {};

  for (const [key, value] of Object.entries(data)) {
    // PHI fields that should be hashed
    if (key === 'firstName' || key === 'first_name') {
      sanitized.firstNameHash = value
        ? await hashValue(String(value))
        : undefined;
      continue;
    }

    if (key === 'lastName' || key === 'last_name') {
      sanitized.lastNameHash = value
        ? await hashValue(String(value))
        : undefined;
      continue;
    }

    if (key === 'email') {
      sanitized.emailHash = value ? await hashValue(String(value)) : undefined;
      continue;
    }

    if (key === 'phone') {
      sanitized.phoneHash = value ? await hashValue(String(value)) : undefined;
      continue;
    }

    // Remove other potential PHI fields entirely
    if (key === 'name' || key === 'fullName' || key === 'full_name') {
      sanitized.nameHash = value ? await hashValue(String(value)) : undefined;
      continue;
    }

    // Safe non-PHI fields can pass through
    // IDs, timestamps, scores, statuses, etc.
    const safeFields = [
      'assessmentId',
      'assessment_id',
      'practiceId',
      'practice_id',
      'tenantId',
      'tenant_id',
      'saxId',
      'sax_id',
      'riskScore',
      'risk_score',
      'contactType',
      'contact_type',
      'formId',
      'form_id',
      'bookingId',
      'booking_id',
      'appointmentDate',
      'appointment_date',
      'selectedDate',
      'selected_date',
      'selectedTime',
      'selected_time',
      'retryCount',
      'retry_count',
      'timestamp',
      'date',
      'time',
      'status',
      'result',
      'success',
      '_timestamp',
      '_currentPage',
      '_onLine',
      '_ipAddress',
      '_ipCity',
      '_ipRegion',
      '_ipCountry',
      '_ipCountryCode',
      '_ipIsp',
      '_ipLatitude',
      '_ipLongitude',
      // Patient chart tracking fields
      'chartId',
      'chartCount',
      'tabName',
      'tabIndex',
      'previousTab',
      'chartIndex',
      'previousChartId',
      'documentId',
      'documentType',
      'fileSize',
      'noteId',
      'parentNoteId',
      'hasAttachments',
      'visibility',
      'errorMessage',
      'questionCount',
      'hasFileUpload',
      'redirectUrlDomain',
      // Appointments tracking fields
      'bookingCode',
      'appointmentType',
      'totalCount',
      'page',
      'eventCount',
      'filterType',
    ];

    if (safeFields.includes(key)) {
      sanitized[key] = value;
      continue;
    }

    // For unknown fields, be conservative and skip them
    // This ensures we don't accidentally leak PHI
    console.debug(`[PHI Sanitization] Skipping unknown field: ${key}`);
  }

  return sanitized;
}

/**
 * Sanitize login tracking data
 * Specific sanitization for login events
 */
export async function sanitizeLoginData(
  data: Record<string, any>,
): Promise<Record<string, any>> {
  const sanitized = await sanitizeTrackingData(data);

  // Additional login-specific sanitization
  // Remove token-related fields
  const tokenFields = [
    'access_token',
    'id_token',
    'refresh_token',
    'token',
    'session_token',
    'api_key',
  ];

  for (const field of tokenFields) {
    if (sanitized[field]) {
      delete sanitized[field];
    }
  }

  return sanitized;
}

/**
 * Check if a value contains potential PHI
 * Used for validation and warnings
 */
export function containsPHI(value: any): boolean {
  if (!value) return false;

  const str = String(value).toLowerCase();

  // Check for common PHI indicators
  const phiIndicators = [
    '@', // Email addresses
    'name',
    'phone',
    'address',
    'ssn',
    'dob',
    'birth',
  ];

  return phiIndicators.some((indicator) => str.includes(indicator));
}
